import { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

const fmt = (s) => {
  if (!Number.isFinite(s)) return "--:--";
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};
const isBeat = (spk) => spk === "DIRECTION" || spk === "SFX";

function Reel({ spinning }) {
  return (
    <svg viewBox="0 0 60 60" className={`w-20 h-20 ${spinning ? "reel-spin" : ""}`} aria-hidden>
      <circle cx="30" cy="30" r="27" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.6" />
      <circle cx="30" cy="30" r="6" fill="currentColor" />
      {[0, 120, 240].map((a) => (
        <rect key={a} x="27" y="8" width="6" height="12" fill="currentColor" opacity="0.5" transform={`rotate(${a} 30 30)`} />
      ))}
    </svg>
  );
}

export function TapePlayer({ src, lines, label, sublabel, onEnded, variant = "tape", progressive = false, testid = "tape" }) {
  const audioRef = useRef(null);
  const listRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(NaN);
  const [failed, setFailed] = useState(!src);
  const [ended, setEnded] = useState(false);

  const cum = useMemo(() => {
    const w = lines.map(([spk, txt]) => (isBeat(spk) ? 28 : txt.length + 14));
    const total = w.reduce((a, b) => a + b, 0);
    let acc = 0;
    return w.map((x) => (acc += x) / total);
  }, [lines]);
  const frac = dur ? t / dur : 0;
  const active = ended ? lines.length - 1 : cum.findIndex((c) => frac <= c);

  useEffect(() => setFailed(!src), [src]);

  useEffect(() => {
    const box = listRef.current;
    const el = box && box.querySelector(`[data-line="${active}"]`);
    if (el && playing) box.scrollTo({ top: el.offsetTop - box.clientHeight / 3, behavior: "smooth" });
  }, [active, playing]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        await a.play();
      } catch (e) {
        setFailed(true);
      }
    } else a.pause();
  };

  const shown = progressive && !ended ? lines.slice(0, Math.max(0, active + (playing || t > 0 ? 1 : 0))) : lines;

  return (
    <div className="grid grid-cols-12 gap-8 items-start" data-testid={`${testid}-player`}>
      <div className="col-span-12 lg:col-span-5 player-card">
        <div className="px-7 py-4 border-b border-[#b1b4b6] flex items-center justify-between">
          <span className="font-mono text-[15px] tracking-[0.1em] font-semibold">{label}</span>
          {playing && <span className="flex items-center gap-2 font-mono text-sm text-[#d4351c] font-bold"><span className="w-2.5 h-2.5 rounded-full bg-[#d4351c] rec-dot" /> PLAYBACK</span>}
        </div>
        <div className="p-7">
          {sublabel && <p className="text-xl font-bold">{sublabel}</p>}
          <div className="flex items-center justify-center gap-10 my-7 text-[#505a5f]">
            <Reel spinning={playing} />
            <Reel spinning={playing} />
          </div>
          {failed ? (
            <div className="warning-text" data-testid={`${testid}-damaged`}>
              <span className="warning-icon" aria-hidden>!</span> <strong>Tape damaged — read the transcript</strong>
            </div>
          ) : (
            <>
              <input
                type="range" className="scrub" min={0} max={Number.isFinite(dur) ? dur : 0} step={0.1} value={t}
                onChange={(e) => { audioRef.current.currentTime = Number(e.target.value); setT(Number(e.target.value)); }}
                data-testid={`${testid}-scrubber`} aria-label="Seek"
              />
              <div className="flex justify-between font-mono text-base mt-2 opacity-80">
                <span data-testid={`${testid}-time`}>{fmt(t)}</span><span>{fmt(dur)}</span>
              </div>
              <div className="flex gap-3 mt-6">
                <button className="btn-spore flex-1" onClick={toggle} data-testid={`${testid}-play-button`}>
                  {playing ? <><Pause size={20} /> Pause</> : <><Play size={20} /> {ended && dur && t >= dur - 0.5 ? "Replay" : t > 0 ? "Resume" : "Play recording"}</>}
                </button>
                <button className="btn-ghost" onClick={() => { audioRef.current.currentTime = 0; setT(0); }} data-testid={`${testid}-restart-button`} aria-label="Restart">
                  <RotateCcw size={18} />
                </button>
              </div>
              <audio
                ref={audioRef} src={src} preload="auto"
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
                onError={() => setFailed(true)}
                onEnded={() => { setPlaying(false); setEnded(true); onEnded && onEnded(); }}
                data-testid={`${testid}-audio`}
              />
            </>
          )}
        </div>
      </div>
      <div className="col-span-12 lg:col-span-7 transcript-box">
        <div className="px-6 py-4 border-b border-[#b1b4b6] flex items-center justify-between">
          <span className="text-xl font-black">Transcript</span>
          <span className="font-mono text-sm opacity-70">{lines.filter(([sp]) => !isBeat(sp)).length} statements</span>
        </div>
        <div ref={listRef} className="relative max-h-[62vh] overflow-y-auto thin-scroll px-6 py-5 space-y-4" data-testid={`${testid}-transcript`}>
          {shown.map(([spk, txt], i) => (
            <div key={i} data-line={i} className={`transition-opacity duration-300 ${playing && i !== active ? "opacity-55" : "opacity-100"}`}>
              {isBeat(spk) ? (
                <p className="t-beat">[{txt}]</p>
              ) : (
                <p className="text-[19px] leading-relaxed">
                  <span className={`t-speaker ${spk === "INTERVIEWER" ? "t-interviewer" : "t-suspect"}`}>{spk}</span>
                  {txt}
                </p>
              )}
            </div>
          ))}
          {progressive && !ended && shown.length === 0 && <p className="italic opacity-60">The transcript surfaces as the recording plays.</p>}
        </div>
      </div>
    </div>
  );
}
